<script>
import { iFeaturedIconSizes, iFeaturedIconTypes } from '@inscribe/signal';
import { RISKS_CONFIG } from '@/constants/analyst-config';
import DocumentReviewRiskSummaryButton from '@/V2/documents/document-view/document-review-agent/DocumentReviewRiskSummaryButton.vue';
import palette from '@inscribe/signal/dist/palette';
import { RISK_LEVEL, RUN_STATUS } from '@/constants/analyst-constants';
import { analystTrustScoreRank } from '@/utils/trustScoreRank';
import TrustScoreDisplay, {
  TrustScoreDisplaySizes,
} from '@/V2/documents/TrustScoreDisplay';
import StatusCard from '@/V2/components/status/StatusCard.vue';
import { Statuses } from '@/V2/components/status/utils';
import {
  mapRunToStatus,
  mapRunToLabel,
} from '@/V2/documents/document-view/document-review-agent/utils';

export default {
  components: {
    DocumentReviewRiskSummaryButton,
    StatusCard,
    TrustScoreDisplay,
  },
  props: {
    document: {
      type: Object,
      default: null,
    },
    lastRun: {
      type: Object,
      default: null,
    },
    subtitles: {
      type: Object,
      default: null,
    },
    hasVerifications: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      RISKS_CONFIG,
      iFeaturedIconSizes,
      iFeaturedIconTypes,
      palette,
      mapRunToStatus,
      mapRunToLabel,
      Statuses,
      TrustScoreDisplaySizes,
    };
  },
  computed: {
    boxBackground() {
      let background = palette[`light-neutral-30`];

      if (this.lastRun.status === RUN_STATUS.FINISHED) {
        const riskLevel = this.lastRun.output?.risk_level;
        if (riskLevel === RISK_LEVEL.HIGH) {
          background = palette[`light-error-30`];
        } else if (riskLevel === RISK_LEVEL.MEDIUM) {
          background = palette[`light-warning-30`];
        } else if (riskLevel === RISK_LEVEL.LOW) {
          background = palette[`light-success-30`];
        }
      }

      return background;
    },
    trustScoreSubtitle() {
      return analystTrustScoreRank(this.lastRun?.output?.trust_score, false);
    },
    hasTrustScore() {
      return ![undefined, null].includes(this.lastRun?.output?.trust_score);
    },
  },
  methods: {
    openScreen(screen) {
      this.$emit('open-risk-summary', screen);
    },
  },
};
</script>

<template>
  <div
    class="risk-summary d-flex flex-column i-p-10 i-gap-10"
    data-testid="document-review-risk-summary"
  >
    <!-- header -->
    <div class="header d-flex align-items-start i-gap-6">
      <div class="d-flex flex-column flex-grow-1">
        <div class="title d-flex i-text-button-2 i-text-neutral-120">
          Risk Rating
        </div>
        <div class="subtitle d-flex i-text-body-3 i-text-neutral-110">
          Factors determining risk level
        </div>
      </div>
    </div>
    <div
      :style="{ background: boxBackground }"
      class="summary-data d-flex flex-column i-p-4 i-gap-4 i-radius-2xl"
    >
      <status-card
        :state="mapRunToStatus(lastRun)"
        :label="mapRunToLabel(lastRun)"
        :score="lastRun?.output?.trust_score"
        :hover-state="false"
      ></status-card>
      <div class="detail-links d-flex flex-column i-gap-1">
        <DocumentReviewRiskSummaryButton
          v-if="hasTrustScore"
          title="Trust Score"
          :subtitle="trustScoreSubtitle"
          :has-icon="false"
          @on-button-click="openScreen('trustScore')"
        >
          <template #icon>
            <trust-score-display
              class="trust-score"
              :score="lastRun?.output?.trust_score"
              :size="TrustScoreDisplaySizes.MEDIUM"
              :is-analyst="true"
            />
          </template>
        </DocumentReviewRiskSummaryButton>
        <DocumentReviewRiskSummaryButton
          title="Signals"
          :subtitle="subtitles['signals']"
          @on-button-click="openScreen('signals')"
        ></DocumentReviewRiskSummaryButton>
        <DocumentReviewRiskSummaryButton
          v-if="hasVerifications"
          title="Verifications"
          :subtitle="subtitles['verifications']"
          @on-button-click="openScreen('verifications')"
        ></DocumentReviewRiskSummaryButton>
        <DocumentReviewRiskSummaryButton
          title="Requirements"
          :subtitle="subtitles['requirements']"
          @on-button-click="openScreen('requirements')"
        ></DocumentReviewRiskSummaryButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.risk-summary {
  .circle {
    width: 40px;
    height: 40px;
    border-radius: 100px;
    background: $light-neutral-60;
  }

  .link {
    height: 64px;
    background: white;
    cursor: pointer;

    &:hover {
      background: $light-neutral-20;
    }

    &:first-of-type {
      border-top-left-radius: 12px;
      border-top-right-radius: 12px;
    }

    &:last-of-type {
      border-bottom-left-radius: 12px;
      border-bottom-right-radius: 12px;
    }
  }
}
</style>
